import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Newspaper, Mail, Building2, Compass, CalendarDays,
  Award, TrendingUp, Users, ClipboardCheck, Folder, Bell, Settings,
  UserCircle, LogOut, Menu, X, Flame, Trash2, Home, Images, Wallet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const ALL_ROLES = ["developer", "national_admin", "regional_admin", "chapter_leader", "scout_leader", "cubs_leader", "patrol_leader", "patrol_co_leader", "scout", "parent", "consultant"];
const LEADER_TIER = ["developer", "national_admin", "regional_admin", "chapter_leader", "scout_leader", "cubs_leader", "patrol_leader", "patrol_co_leader"];
const NON_SCOUT = [...LEADER_TIER, "consultant"];

const ALL_ITEMS = [
  { to: "/dashboard", label: "Dashboard", labelHy: "Վահանակ", icon: LayoutDashboard, roles: ALL_ROLES },
  { to: "/announcements", label: "News", labelHy: "Նորություններ", icon: Newspaper, roles: ALL_ROLES },
  { to: "/newsletters", label: "Newsletters", labelHy: "Տեղեկագիր", icon: Mail, roles: ALL_ROLES },
  { to: "/chapters", label: "Chapters", labelHy: "Մասնաճյուղեր", icon: Building2, roles: ALL_ROLES },
  { to: "/programs", label: "Programs", labelHy: "Ծրագրեր", icon: Compass, roles: ALL_ROLES },
  { to: "/calendar", label: "Calendar", labelHy: "Օրացույց", icon: CalendarDays, roles: ALL_ROLES },
  { to: "/badges", label: "Progress Badges", labelHy: "Կրծքանշաններ", icon: Award, roles: ALL_ROLES },
  { to: "/my-progress", label: "My Progress", labelHy: "Իմ առաջընթացը", icon: TrendingUp, roles: ["scout"] },
  { to: "/members", label: "Members", labelHy: "Անդամներ", icon: Users, roles: NON_SCOUT },
  { to: "/attendance", label: "Attendance", labelHy: "Հաճախում", icon: ClipboardCheck, roles: NON_SCOUT },
  { to: "/resources", label: "Resources", labelHy: "Ռեսուրսներ", icon: Folder, roles: ALL_ROLES },
  { to: "/galleries", label: "Galleries", labelHy: "Պատկերասրահ", icon: Images, roles: ALL_ROLES },
  { to: "/notifications", label: "Notifications", labelHy: "Ծանուցումներ", icon: Bell, roles: ALL_ROLES },
  { to: "/administration", label: "Administration", labelHy: "Կառավարում", icon: Settings, roles: LEADER_TIER },
  { to: "/finance", label: "Finance", labelHy: "Ֆինանսներ", icon: Wallet, roles: LEADER_TIER },
  { to: "/trash", label: "Trash Bin", labelHy: "Աղբարկղ", icon: Trash2, roles: ["developer", "national_admin", "regional_admin"] },
  { to: "/profile", label: "Profile", labelHy: "Պրոֆիլ", icon: UserCircle, roles: ALL_ROLES },
];

export default function Sidebar({ mobileOpen, setMobileOpen, lang }) {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const items = ALL_ITEMS.filter((i) => i.roles.includes(user?.role) || (user?.roles || []).some(r => i.roles.includes(r)));
  const isLeader = user?.role && LEADER_TIER.includes(user.role);
  const [pendingBadgeRequests, setPendingBadgeRequests] = useState(0);
  const [programAttention, setProgramAttention] = useState({ count: 0, unpaid: 0, waitlisted: 0 });

  useEffect(() => {
    if (!isLeader) { setPendingBadgeRequests(0); setProgramAttention({ count: 0, unpaid: 0, waitlisted: 0 }); return; }
    let alive = true;
    const fetchAll = async () => {
      try {
        const [b, p] = await Promise.all([
          api.get("/badges/requests/count"),
          api.get("/programs/attention-count"),
        ]);
        if (alive) {
          setPendingBadgeRequests(b.data?.count || 0);
          setProgramAttention(p.data || { count: 0, unpaid: 0, waitlisted: 0 });
        }
      } catch (err) {
        if (alive) console.warn("sidebar counts failed:", err?.message);
      }
    };
    fetchAll();
    const id = setInterval(fetchAll, 60000);
    const onFocus = () => fetchAll();
    window.addEventListener("focus", onFocus);
    window.addEventListener("badge-requests-changed", fetchAll);
    window.addEventListener("program-registrations-changed", fetchAll);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("badge-requests-changed", fetchAll);
      window.removeEventListener("program-registrations-changed", fetchAll);
    };
  }, [isLeader]);

  const doLogout = async () => { await logout(); nav("/login"); };

  const badgeFor = (to) => {
    if (to === "/badges" && pendingBadgeRequests > 0) {
      return { count: pendingBadgeRequests, title: `${pendingBadgeRequests} pending badge request${pendingBadgeRequests === 1 ? "" : "s"}` };
    }
    if (to === "/programs" && programAttention.count > 0) {
      const parts = [];
      if (programAttention.unpaid) parts.push(`${programAttention.unpaid} unpaid`);
      if (programAttention.waitlisted) parts.push(`${programAttention.waitlisted} waitlisted`);
      return { count: programAttention.count, title: `Needs attention · ${parts.join(" · ")}` };
    }
    return null;
  };

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 flex-shrink-0 z-50 transition-transform lg:transition-none ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        style={{ background: "hsl(152 43% 15%)", color: "hsl(42 30% 94%)" }}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-6">
            <Link to="/" className="flex items-center gap-3 group" title={lang === "hy" ? "Հիմնական էջ" : "Public homepage"} data-testid="sidebar-logo-home">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/95 shadow-inner border-2 border-[#F4F1EA]/40 group-hover:scale-105 transition-transform overflow-hidden p-1">
                <img src="/brand/homenetmen-logo.webp" alt="Homenetmen HASK" className="w-full h-full object-contain"/>
              </div>
              <div>
                <div className="font-display font-black text-base leading-none flex items-center gap-1.5">HOMENETMEN <Home size={11} className="opacity-40 group-hover:opacity-90 transition-opacity"/></div>
                <div className="text-[10px] tracking-[0.24em] uppercase opacity-70">ՀՄԸՄ-ՀԱՍԿ · EST. 1989</div>
              </div>
            </Link>
            <button className="lg:hidden" onClick={() => setMobileOpen(false)} data-testid="close-sidebar-btn">
              <X size={22} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 space-y-1 pb-6">
            {items.map((it) => {
              const info = badgeFor(it.to);
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/dashboard"}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <it.icon size={18} strokeWidth={2.2} />
                  <span className="text-sm font-medium flex-1">{t(it.label, it.labelHy)}</span>
                  {info && info.count > 0 && (
                    <span
                      className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold text-white shadow-sm"
                      style={{ background: "hsl(0 72% 55%)" }}
                      data-testid={`nav-badge-count-${it.to.replace(/\//g, "-")}`}
                      title={info.title}
                    >
                      {info.count > 99 ? "99+" : info.count}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="px-4 pb-6">
            <div className="rounded-2xl p-4 mb-3 flex items-center gap-3" style={{ background: "rgba(244,241,234,0.06)" }}>
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0"/>
              ) : (
                <div className="w-10 h-10 rounded-full bg-[hsl(12,65%,63%)] text-white flex items-center justify-center font-bold flex-shrink-0">{user?.name?.[0]}</div>
              )}
              <div className="min-w-0">
                <div className="text-xs opacity-70" data-testid="sidebar-user-roles">{(user?.roles?.length ? user.roles : [user?.role]).map((r, i) => <span key={r}>{i > 0 && " + "}<span>{r?.replace("_", " ").toUpperCase()}</span></span>)}</div>
                <div className="font-semibold truncate text-sm">{user?.name}</div>
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={doLogout}
              className="w-full btn-pill bg-[hsl(12,65%,63%)] hover:bg-[hsl(12,70%,55%)] text-white border-0"
              data-testid="logout-btn"
            >
              <LogOut size={16} className="mr-2" /> {lang === "hy" ? "Ելք" : "Log out"}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}

export function MobileTopbar({ setMobileOpen, lang, setLang, canToggle }) {
  return (
    <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur border-b border-border sticky top-0 z-30">
      <button onClick={() => setMobileOpen(true)} data-testid="open-sidebar-btn">
        <Menu size={22} />
      </button>
      <div className="font-display font-black text-sm">HOMENETMEN HASK</div>
      {canToggle ? <button
        onClick={() => setLang(lang === "hy" ? "en" : "hy")}
        className="text-xs font-bold uppercase tracking-wider"
        data-testid="lang-toggle-mobile"
      >
        {lang === "hy" ? "EN" : "ՀԱՅ"}
      </button> : <span className="w-6"/>}
    </div>
  );
}
